import { useState, useEffect, useContext } from 'react';
import { BedethequeContext } from '../../utils/context/bedetheque';
import { updateAlbumAuteurs, updateAuteur } from '../../libs/apiController';
import AuteurAutocomplete from './AuteurAutocomplete';
import ElementInputMaker from './ElementInputMaker';

const ICONS = { scenariste: '🖊️', dessinateur: '🖌️' };

/*
  Puce d'un auteur en mode édition.
  - Double-clic sur le nom → renomme l'auteur PARTOUT (document partagé) — utile pour
    corriger une coquille. Réservé aux auteurs déjà en base (avec _id).
  - ❌ → retire la référence DE CET album uniquement.
*/
function AuteurChip({ auteur, icon, onRemove, onRename }) {
    const [showInput, setShowInput] = useState(false);
    const [nom, setNom] = useState(auteur.nom);

    useEffect(() => setNom(auteur.nom), [auteur.nom]);

    return (
        <span className="serie-speech-arrow-none auteur-chip">
            {icon}
            {auteur._id ? (
                <ElementInputMaker
                    value={nom}
                    handleChange={(e) => setNom(e.target.value)}
                    handleDoubleClick={() => setShowInput(true)}
                    handleBlur={() => { setShowInput(false); onRename(auteur, nom); }}
                    showInputEle={showInput}
                    size={Math.max((nom?.length || 6) - 2, 4)}
                />
            ) : (
                <span>{auteur.nom}</span>
            )}
            <span className="auteur-remove" title="Retirer de cet album" onClick={onRemove}>❌</span>
        </span>
    );
}

/*
  Édition des auteurs d'un album — affiché UNIQUEMENT en mode édition.
  Deux sémantiques selon le geste :
   - ❌ / ➕  → remplacement PAR RÉFÉRENCE : on ne touche qu'à cet album. À la saisie,
     un auteur connu est réutilisé (résolu par son nom), sinon créé côté serveur.
   - double-clic sur un nom → renommage GLOBAL de l'auteur (impacte tous les albums).
*/
function AuteursEditor({ album }) {
    const { auteurs, setReload } = useContext(BedethequeContext);
    const [scenaristes, setScenaristes] = useState(album.scenaristesNameList || []);
    const [dessinateurs, setDessinateurs] = useState(album.dessinateursNameList || []);
    const [adding, setAdding] = useState(null); // 'scenariste' | 'dessinateur' | null
    const [input, setInput] = useState('');

    // Resynchronise sur les données fraîches après chaque refetch (l'objet `album`
    // change de référence) : l'état optimiste cède la place à la vérité serveur,
    // et un auteur fraîchement créé récupère son _id (donc devient renommable).
    useEffect(() => {
        setScenaristes(album.scenaristesNameList || []);
        setDessinateurs(album.dessinateursNameList || []);
    }, [album]);

    // Persiste les références de CET album. N'envoie que {_id} (auteur connu) ou {nom}
    // (à résoudre/créer côté serveur via resolveAuteurRefs).
    async function persist(nextSce, nextDes) {
        const toRef = (a) => (a._id ? { _id: a._id } : { nom: a.nom });
        await updateAlbumAuteurs(album._id, {
            scenaristes: nextSce.map(toRef),
            dessinateurs: nextDes.map(toRef),
        });
        setReload(true);
    }

    // Match insensible à la casse sur les auteurs existants → réutilisation.
    function resolveByName(nom) {
        const found = (auteurs || []).find((a) => a.nom.toLowerCase() === nom.trim().toLowerCase());
        return found || { nom: nom.trim() };
    }

    function addAuteur(role, nom) {
        if (!nom?.trim()) return;
        const auteur = resolveByName(nom);
        if (role === 'scenariste') {
            const next = [...scenaristes, auteur];
            setScenaristes(next);
            persist(next, dessinateurs);
        } else {
            const next = [...dessinateurs, auteur];
            setDessinateurs(next);
            persist(scenaristes, next);
        }
        setAdding(null);
        setInput('');
    }

    function removeAuteur(role, idx) {
        if (role === 'scenariste') {
            const next = scenaristes.filter((_, i) => i !== idx);
            setScenaristes(next);
            persist(next, dessinateurs);
        } else {
            const next = dessinateurs.filter((_, i) => i !== idx);
            setDessinateurs(next);
            persist(scenaristes, next);
        }
    }

    // Renommage global du document auteur partagé (toutes les listes le référençant suivent).
    async function renameAuteur(auteur, newNom) {
        const nom = newNom?.trim();
        if (!auteur._id || !nom || nom === auteur.nom) return;
        await updateAuteur(auteur._id, { nom });
        const apply = (list) => list.map((a) => (a._id === auteur._id ? { ...a, nom } : a));
        setScenaristes(apply);
        setDessinateurs(apply);
        setReload(true);
    }

    const renderGroup = (role, list) => (
        <div className="auteurs-edit-group">
            {list.map((a, idx) => (
                <AuteurChip
                    key={`${a._id || a.nom}+${idx}`}
                    auteur={a}
                    icon={ICONS[role]}
                    onRemove={() => removeAuteur(role, idx)}
                    onRename={renameAuteur}
                />
            ))}
            {adding === role ? (
                <AuteurAutocomplete
                    icon={ICONS[role]}
                    placeholder="Nom de l'auteur"
                    value={input}
                    autoFocus
                    onChange={setInput}
                    onItemSubmit={(item) => addAuteur(role, item.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addAuteur(role, input); }}
                    onBlur={() => setTimeout(() => { setAdding(null); setInput(''); }, 150)}
                />
            ) : (
                <span className="serie-speech-arrow-none auteur-add" title="Ajouter" onClick={() => { setAdding(role); setInput(''); }}>
                    {ICONS[role]}➕
                </span>
            )}
        </div>
    );

    return (
        <div className="auteurs-editor">
            {renderGroup('scenariste', scenaristes)}
            {renderGroup('dessinateur', dessinateurs)}
        </div>
    );
}

export default AuteursEditor;
